import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { splitLangPath, toMarketingLang, withLang } from "@/lib/langRoutes";

const SITE_URL = "https://sida.one";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

const OG_LOCALE: Record<string, string> = { ar: "ar_SA", en: "en_US", fr: "fr_FR" };
const HTML_DIR: Record<string, "rtl" | "ltr"> = { ar: "rtl", en: "ltr", fr: "ltr" };

interface SEOProps {
  title: string;
  description: string;
  /** Either a full URL or a language-neutral path; if omitted, uses the current route. */
  canonical?: string;
  path?: string;
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  jsonLd?: object | object[];
  locale?: string;
}

export function SEO({
  title,
  description,
  canonical,
  path,
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd,
  locale,
}: SEOProps) {
  const location = useLocation();
  const { lang: pathLang, basePath: currentBase } = splitLangPath(location.pathname);

  // Legacy "?lng=xx" URLs are client-side redirected to the prefixed route.
  // Emit the redirect *target* as the canonical/hreflang language so crawlers
  // consolidate /?lng=en into /en instead of reporting an unindexed redirect.
  const lngParam = new URLSearchParams(location.search).get("lng");
  const lang = lngParam ? toMarketingLang(lngParam) : pathLang;


  // A page may pass an explicit language-neutral path; otherwise use the route.
  const explicit = canonical || path;
  const isAbsolute = !!explicit && /^https?:\/\//i.test(explicit);
  const basePath = isAbsolute ? currentBase : splitLangPath(explicit || currentBase).basePath;

  // Self-referencing canonical: every language version points at its own URL.
  const url = isAbsolute ? (explicit as string) : `${SITE_URL}${withLang(basePath, lang)}`;

  const altAr = `${SITE_URL}${withLang(basePath, "ar")}`;
  const altEn = `${SITE_URL}${withLang(basePath, "en")}`;
  const altFr = `${SITE_URL}${withLang(basePath, "fr")}`;

  const fullTitle = title.includes("SIDA") ? title : `${title} | SIDA`;
  const ogImage = image && /^https?:\/\//i.test(image) ? image : `${SITE_URL}${image || ""}`;
  const lds = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  // index.html ships a static Arabic description as a no-JS fallback. Once a
  // route renders its own localized description, drop the static duplicate so
  // crawlers never see two conflicting <meta name="description"> tags.
  useEffect(() => {
    document
      .querySelectorAll('head > meta[name="description"]:not([data-rh])')
      .forEach((el) => el.remove());
  }, [description]);

  return (
    <Helmet htmlAttributes={{ lang, dir: HTML_DIR[lang] || "rtl" }}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Reciprocal hreflang across the permanent language routes. */}
      <link rel="alternate" hrefLang="ar" href={altAr} />
      <link rel="alternate" hrefLang="en" href={altEn} />
      <link rel="alternate" hrefLang="fr" href={altFr} />
      <link rel="alternate" hrefLang="x-default" href={altAr} />

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={locale || OG_LOCALE[lang] || "ar_SA"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {lds.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

